import { useMemo, useCallback, useState, useRef } from 'react';

import { FiMousePointer } from 'react-icons/fi';
import { useShallow } from 'zustand/react/shallow';

import { useRoomStore, useMagicPenStore, useRoomConfigStore } from '@src/stores';

import { IToolItem } from '@src/types/magicPen.types';
import { EMagicPenColor, EMagicPenShape, TToolType } from '@src/types';
import type { IInfoModalContent } from '@src/types/stores/roomStore.types';

import {
  PenIcon,
  TextIcon,
  SaveIcon,
  ShapesIcon,
  EraserIcon,
  StickersIcon,
  ClearAllIcon,
  OpenArrowIcon,
  StickyNoteIcon,
  InviteUserIcon,
  DisappearingInkIcon,
} from '@src/assets/icons';

import { useRoomContext } from '@src/contexts';

import { clearAllAnnotations } from '@src/components/MagicPen/services';

import styles from './magicPenTools.module.scss';

const IconWithArrow = ({ Icon }: { Icon: React.FunctionComponent<React.SVGProps<SVGSVGElement>> }) => (
  <div className={styles.button}>
    <div className={styles.iconWithArrow}>
      <Icon />
      <OpenArrowIcon className={styles.arrowIcon} />
    </div>
  </div>
);

interface IUseMagicPenToolsProps {
  onClose: (toolbarPosition?: { x: number; y: number }) => void;
  buttonPosition?: { x: number; y: number };
}

const useMagicPenTools = ({ onClose, buttonPosition }: IUseMagicPenToolsProps) => {
  const nodeRef = useRef<HTMLDivElement>(null);

  // Get states from magicPen store
  const {
    objects,
    activeTool,
    brushColor,
    selectedId,
    isSaveModalOpen,
    selectedTextRange,
    isTextColorChangeMode,
    setObjects,
    resetStates,
    setBrushColor,
    setActiveTool,
    setActiveShape,
    resetAllStates,
    setEraserStrokes,
    setActiveSticker,
    setSaveModalState,
    updateStickyNoteColor,
    setLastSelectedToolIcon,
    updateStickyNoteTextColor,
  } = useMagicPenStore(
    useShallow((s) => ({
      objects: s.objects,
      selectedId: s.selectedId,
      brushColor: s.brushColor,
      activeTool: s.activeTool,
      isSaveModalOpen: s.isSaveModalOpen,
      selectedTextRange: s.selectedTextRange,
      isTextColorChangeMode: s.isTextColorChangeMode,
      setObjects: s.setObjects,
      resetStates: s.resetStates,
      setActiveTool: s.setActiveTool,
      setBrushColor: s.setBrushColor,
      setActiveShape: s.setActiveShape,
      resetAllStates: s.resetAllStates,
      setEraserStrokes: s.setEraserStrokes,
      setActiveSticker: s.setActiveSticker,
      setSaveModalState: s.setSaveModalState,
      updateStickyNoteColor: s.updateStickyNoteColor,
      setLastSelectedToolIcon: s.setLastSelectedToolIcon,
      updateStickyNoteTextColor: s.updateStickyNoteTextColor,
    }))
  );

  const { setInviteToMagicPenModal, setIsInfoModalOpen, setInfoModalContent, hasMagicPenAnnotatePermission } =
    useRoomStore(
      useShallow((s) => ({
        setInviteToMagicPenModal: s.setInviteToMagicPenModal,
        setIsInfoModalOpen: s.setIsInfoModalOpen,
        setInfoModalContent: s.setInfoModalContent,
        hasMagicPenAnnotatePermission: s.hasMagicPenAnnotatePermission,
      }))
    );

  const {
    isClearAllButtonEnabled,
    isEraserOthersObjButtonEnabled,
    isInviteToAnnotateButtonEnabled,
    isSaveCanvasButtonEnabled,
  } = useRoomConfigStore(
    useShallow((s) => ({
      isClearAllButtonEnabled: s.magicPenConfig?.clearAllButton,
      isEraserOthersObjButtonEnabled: s.magicPenConfig?.eraserOthersObjButton,
      isInviteToAnnotateButtonEnabled: s.magicPenConfig?.inviteToAnnotateButton,
      isSaveCanvasButtonEnabled: s.magicPenConfig?.saveCanvasButton,
    }))
  );

  const { canAnnotate, isHost, isActingHost, isCurrentPeerSharing } = useRoomContext();

  // Granted annotators who are not hosts, acting hosts, or screen sharers
  const isGrantedNonHostNonSharer = hasMagicPenAnnotatePermission && !isHost && !isActingHost && !isCurrentPeerSharing;

  // Determine if submenu should be shown (HOST/COHOST if host left/Screen Sharer)
  const shouldShowClearAllSubmenu = useMemo(() => {
    return isHost || isActingHost || isCurrentPeerSharing;
  }, [isHost, isActingHost, isCurrentPeerSharing]);

  // ===== LOCAL STATE =====
  const [currentPosition, setCurrentPosition] = useState<{ x: number; y: number }>(buttonPosition || { x: 0, y: 0 });

  // ===== COMPUTED VALUES =====
  // Check if a sticky note is selected (memoized to avoid repeated find operations)
  const isStickyNoteSelected = useMemo(() => {
    return (
      ['select', 'sticky'].includes(activeTool) &&
      selectedId &&
      objects.find((obj) => obj.id === selectedId && obj.type === EMagicPenShape.STICKY_NOTE)
    );
  }, [activeTool, selectedId, objects]);

  const selectedStickyNote = useMemo(() => {
    return isStickyNoteSelected ? objects.find((obj) => obj.id === selectedId) : null;
  }, [isStickyNoteSelected, objects, selectedId]);

  // Check if text is selected within a sticky note
  const isTextSelected = useMemo(() => {
    return isStickyNoteSelected && isTextColorChangeMode;
  }, [isStickyNoteSelected, isTextColorChangeMode]);

  // ===== CALLBACKS =====
  const handleClearAll = useCallback(() => {
    if (objects.length === 0) {
      return;
    }

    // Show confirmation modal before clearing all objects
    const modalContent: IInfoModalContent = {
      title: 'Clear all annotations?',
      description: 'This action will permanently remove all annotations from the canvas. It cannot be undone.',
      buttons: [
        {
          text: 'Cancel',
          variant: 'primary',
          outlined: true,
          onClick: () => {
            setIsInfoModalOpen(false);
            setInfoModalContent(null);
          },
        },
        {
          text: 'Clear All',
          variant: 'danger',
          onClick: async () => {
            await clearAllAnnotations(false);
            setEraserStrokes([]);
            resetAllStates();
            setIsInfoModalOpen(false);
            setInfoModalContent(null);
          },
        },
      ],
    };

    setInfoModalContent(modalContent);
    setIsInfoModalOpen(true);
  }, [objects.length, resetAllStates, setEraserStrokes, setInfoModalContent, setIsInfoModalOpen]);

  const handleClearMyAnnotations = useCallback(async () => {
    await clearAllAnnotations(true);
  }, []);

  const handleToolChange = useCallback(
    (tool: TToolType) => {
      // Reset any in-progress drawing
      resetStates();

      // Tools that simply set the active tool to themselves
      const basicTools: TToolType[] = ['select', 'eraser', 'pen', 'ephemeralPen', 'text', 'line', 'sticky'];

      if (basicTools.includes(tool)) {
        setActiveTool(tool);
        return;
      }

      // Special cases that require custom handling
      switch (tool) {
        case 'clearAll':
          // For attendees with permissions, clicking directly clears only their annotations (no modal)
          // For HOST/COHOST/Screen Sharer, the submenu handles it
          if (!shouldShowClearAllSubmenu) {
            setActiveTool('clearAll');
            handleClearMyAnnotations();
          }
          break;
        case 'invite':
          setInviteToMagicPenModal(true);
          break;
        case 'save':
          setSaveModalState({ isSaveModalOpen: true });
          break;
        case 'shapes':
        case 'colorPalette':
          // These tools are handled by their respective sub-menus
          break;
        default:
          setActiveTool('select');
      }
    },
    [
      setInviteToMagicPenModal,
      resetStates,
      setActiveTool,
      setSaveModalState,
      shouldShowClearAllSubmenu,
      handleClearMyAnnotations,
    ]
  );

  const isToolVisible = useCallback(
    (tool: string) => {
      if (canAnnotate) return true;

      const toolVisibilityMap: Record<string, boolean> = {
        clearAll: isClearAllButtonEnabled,
        eraser: isEraserOthersObjButtonEnabled,
        invite: isInviteToAnnotateButtonEnabled,
        save: isSaveCanvasButtonEnabled,
      };

      return toolVisibilityMap[tool] ?? true;
    },
    [
      canAnnotate,
      isClearAllButtonEnabled,
      isEraserOthersObjButtonEnabled,
      isInviteToAnnotateButtonEnabled,
      isSaveCanvasButtonEnabled,
    ]
  );

  const handleShapeCreate = useCallback(
    (shapeType: EMagicPenShape) => {
      resetStates();

      // Line is both a shape and a tool
      if (shapeType === 'line') {
        setActiveTool('line');
        return;
      }

      // For other shapes (rectangle, circle, arrow), set as activeShape (not tool)
      setActiveShape(shapeType);
      setActiveTool('shapes');
    },
    [resetStates, setActiveTool, setActiveShape]
  );

  const handleShapeSelect = useCallback(
    (shape: EMagicPenShape) => {
      handleShapeCreate(shape);
    },
    [handleShapeCreate]
  );

  const handleColorSelect = useCallback(
    (color: EMagicPenColor) => {
      setBrushColor(color);
    },
    [setBrushColor]
  );

  const handleSelectSticker = useCallback(
    (sticker?: string) => {
      resetStates();
      setActiveTool('sticker');
      setActiveSticker(sticker);
    },
    [resetStates, setActiveTool, setActiveSticker]
  );

  const handleStickyNoteColorSelect = useCallback(
    (color: EMagicPenColor) => {
      if (selectedId) {
        const currentStickyNote = objects.find((obj) => obj.id === selectedId);

        // Simple check: if text is selected, change text color; otherwise change background
        if (selectedTextRange && selectedTextRange.start !== selectedTextRange.end) {
          // Text is selected - change text color
          updateStickyNoteTextColor(selectedId, color, selectedTextRange.start, selectedTextRange.end);
        } else {
          // No text selected - change background color
          let textColor = currentStickyNote?.textColor || '#000000';

          updateStickyNoteColor(selectedId, color, textColor);
        }

        // Don't emit updates during editing - wait until user clicks outside (editing completes)
        // The update will be sent in handleTextEditComplete when editing ends
      }
    },
    [selectedId, selectedTextRange, updateStickyNoteColor, updateStickyNoteTextColor, objects]
  );

  const handleDragStop = useCallback((event: any, data: any) => {
    setCurrentPosition({ x: data.x, y: 0 });
  }, []);

  const handleClose = useCallback(() => {
    onClose({ x: currentPosition.x, y: 0 });
  }, [onClose, currentPosition.x]);

  // ===== MEMOIZED VALUES =====
  const magicPenTools = useMemo<IToolItem[]>(() => {
    const baseTools: IToolItem[] = [
      {
        icon: <FiMousePointer fill="var(--font-color)" color="var(--font-color)" />,
        tooltip: 'Select',
        tool: 'select',
      },
      { icon: <IconWithArrow Icon={StickersIcon} />, tooltip: 'Stickers', tool: 'sticker' },
      { icon: <PenIcon />, tooltip: 'Pen', tool: 'pen' },
      { icon: <DisappearingInkIcon />, tooltip: 'Disappearing ink', tool: 'ephemeralPen' },
      { icon: <TextIcon />, tooltip: 'Text', tool: 'text' },
      { icon: <StickyNoteIcon />, tooltip: 'Sticky note', tool: 'sticky' },
      { icon: <IconWithArrow Icon={ShapesIcon} />, tooltip: 'Shapes', tool: 'shapes' },
      {
        icon: (
          <div className={styles.button}>
            <div className={styles.iconWithArrow}>
              <div
                className={styles.colorPaletteIcon}
                style={{
                  backgroundColor:
                    isTextSelected && selectedStickyNote?.textColor
                      ? selectedStickyNote.textColor
                      : isStickyNoteSelected && selectedStickyNote?.backgroundColor
                        ? selectedStickyNote.backgroundColor
                        : brushColor,
                }}
              />
              <OpenArrowIcon className={styles.arrowIcon} />
            </div>
          </div>
        ),
        tooltip: 'Color',
        tool: 'colorPalette',
      },
      { icon: <ClearAllIcon />, tooltip: 'Clear all', isDividerBefore: true, tool: 'clearAll' },
      { icon: <EraserIcon />, tooltip: 'Eraser', tool: 'eraser' },
      {
        icon: <InviteUserIcon />,
        tooltip: 'Let others annotate',
        isDividerBefore: true,
        tool: 'invite',
      },
      { icon: <SaveIcon />, tooltip: 'Save & share', tool: 'save' },
    ];

    // For granted annotators who are not hosts or screen sharers, remove
    // the "Let others annotate" and "Save & share" section entirely,
    // but add a divider after the eraser to replace the removed section's divider.
    if (isGrantedNonHostNonSharer) {
      const filteredTools = baseTools.filter((tool) => !['invite', 'save'].includes(tool.tool));
      // Add divider after eraser (which is now the last tool)
      const eraserIndex = filteredTools.findIndex((tool) => tool.tool === 'eraser');
      if (eraserIndex !== -1) {
        filteredTools[eraserIndex] = { ...filteredTools[eraserIndex], isDividerAfter: true };
      }
      return filteredTools;
    }

    return baseTools;
  }, [
    brushColor,
    isStickyNoteSelected,
    isTextSelected,
    selectedStickyNote?.backgroundColor,
    selectedStickyNote?.textColor,
    isGrantedNonHostNonSharer,
  ]);

  return {
    IconWithArrow,

    nodeRef,
    activeTool,
    brushColor,
    magicPenTools,
    isSaveModalOpen,
    currentPosition,
    selectedStickyNote,
    isStickyNoteSelected,

    handleClose,
    handleDragStop,
    handleToolChange,
    handleShapeSelect,
    handleShapeCreate,
    handleColorSelect,
    setSaveModalState,
    handleSelectSticker,
    setLastSelectedToolIcon,
    handleStickyNoteColorSelect,
    isToolVisible,
    shouldShowClearAllSubmenu,
    handleClearAll,
    handleClearMyAnnotations,
  };
};

export default useMagicPenTools;
